import { useTranslation } from "react-i18next";
import { FC, useEffect, useMemo } from "react";
import { Box, Button, Form, OtpInput, Spinner, Typography } from "@orion/ui";
import { useCodeVerification } from "../hooks";
import QRCode from "react-qr-code";
import { Close } from "@orion/icons";
import { PasswordResetScreen } from "@delorean/forgetPassword";
import { RootStateOrAny, useSelector } from "react-redux";
import { LoginScreen } from "../types";
import { AppDomain } from "@delorean/_shared/constants";

export const QRCodeScreen = () => {
  const { t } = useTranslation();
  const {
    OTP,
    setOTP,
    loading,
    qrCodeSubmit,
    operationCode,
    qrCodeValue,
    setCurrentScreen,
    loadingLoginForQr,
  } = useCodeVerification();

  const screen = useSelector((state: RootStateOrAny) => state.login.screen);

  const isBtnDisabled = useMemo(() => {
    return loading || OTP.length < 6;
  }, [OTP, loading]);

  const goBack = () => {
    if (screen === PasswordResetScreen.APP_QRCODE) {
      setCurrentScreen(PasswordResetScreen.APP_NOTIFICATION);
    }
    if (screen === LoginScreen.APP_QRCODE) {
      setCurrentScreen(LoginScreen.APP_NOTIFICATION);
    }
  };

  return (
    <Box
      flex
      role="form"
      data-landmark="1"
      justifyBetween
      flexDirection="column"
      alignContent="center"
      maxWidth={"3xl"}
      maxHXl
      gap5
    >
       <Box flex column justifyBetween>
        <Box flex justifyEnd textAlign="right" mb10 gap3>
          <Typography textNeutral70>
            {t("login.codeVerification.operationCode")}
          </Typography>
          <Typography textNeutral70 fontBold>
            {operationCode}
          </Typography>
        </Box>
        <Typography fontBold textAlign="left" text2xl>
          {t("login.scanForAccess")}
        </Typography>
        <Typography>
          {t("login.scanQRCode", { appName: AppDomain.BPER })}
        </Typography>
      </Box>
      <Box flex mt10 alignSelf="center">
        {loadingLoginForQr ? (
          <Spinner size={"12"} />
        ) : (
          <QRCode
            style={{ height: "135px", maxWidth: "135px", width: "135px" }}
            value={JSON.stringify(qrCodeValue)}
            viewBox={`0 0 256 256`}
          />
        )}
      </Box>
      <form onSubmit={(e) => qrCodeSubmit(e)}>
        <Box flex column justifyBetween gap5>
          <Box flex justifyCenter mb3>
            <OtpInput
              aria-label={t("accessibility.codeVerification.otp")}
              aria-required="true"
              value={OTP}
              onChange={(input) => setOTP(input.toString())}
              fontBold
            />
            <Button
              p0
              variant="ghostPrimary"
              onClick={() => setOTP("")}
              type="button"
              aria-label={t("accessibility.closeErrorMessage")}
            >
              <Close style={{ width: 24, height: 24 }} />
            </Button>
          </Box>
          <Box
            flex
            justifyCenter
            alignItems="center"
            style={{
              display: "flex",
              justifyContent: "center",
              textAlign: "center",
              margin: "0 auto",
              width: "100%",
            }}
          >
            <Button
              type="submit"
              minWidth={"56"}
              style={{
                margin: "0 auto",
                display: "block",
              }}
            >
              {t("login.confirm")}
            </Button>
          </Box>
        </Box>
      </form>
      <Box flex alignItems="center" justifyCenter alignContent="center">
        <Typography mt8 textAlign="center" textNeutral70>
          {t("login.authFailure.cancelProcedure")}
        </Typography>
        <Button variant={"ghostPrimary"} p0 onClick={() => goBack()}>
          <Typography
            mt8
            ml3
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            {t("login.cancel")}
          </Typography>
        </Button>
      </Box>   
    </Box>
  );
};

export default QRCodeScreen;
